/* =========================
   Maxwell Rose — Notify Me
   + hCaptcha (explicit/invisible)
========================= */

console.log("[MR] notify-me.js loaded", new Date().toISOString());

/* ---------- CONFIG ---------- */
const MR_NOTIFY_ENDPOINT = "https://notify-me.maxwell-2d8.workers.dev";
const MR_HCAPTCHA_SITEKEY = "c2e8631c-286a-4358-9286-8f52a7927ece";
const MR_HCAPTCHA_CONTAINER_ID = "mr-hcaptcha";

/* ---------- hCaptcha State ---------- */
let mrWidgetId = null;
let mrCaptchaReady = false;
let mrResolve = null;
let mrReject = null;

/**
 * Render hCaptcha widget once (explicit mode).
 * Safe to call multiple times.
 */
function mrInitCaptcha() {
  if (!window.hcaptcha) return false;

  // container must exist
  const el = document.getElementById(MR_HCAPTCHA_CONTAINER_ID);
  if (!el) {
    console.warn(`[MR] Missing #${MR_HCAPTCHA_CONTAINER_ID} container`);
    return false;
  }

  if (mrWidgetId != null) {
    mrCaptchaReady = true;
    return true;
  }

  try {
    mrWidgetId = window.hcaptcha.render(MR_HCAPTCHA_CONTAINER_ID, {
      sitekey: MR_HCAPTCHA_SITEKEY,
      size: "invisible",
      callback: mrOnCaptchaDone,
    });

    mrCaptchaReady = true;
    console.log("[MR] hCaptcha widget rendered:", mrWidgetId);
    return true;
  } catch (e) {
    console.error("[MR] hCaptcha render failed:", e);
    return false;
  }
}

/**
 * Promise wrapper to execute captcha and receive token.
 */
function getHCaptchaToken() {
  return new Promise((resolve, reject) => {
    const ok = mrInitCaptcha();
    if (!ok || !mrCaptchaReady || mrWidgetId == null) {
      reject(new Error("Captcha not ready (script still loading or blocked)."));
      return;
    }

    // one-shot resolver for this attempt
    mrResolve = resolve;
    mrReject = reject;

    try {
      window.hcaptcha.execute(mrWidgetId);
    } catch (e) {
      mrResolve = null;
      mrReject = null;
      reject(e);
    }
  });
}

/**
 * Called by hCaptcha after execute() succeeds.
 */
function mrOnCaptchaDone(token) {
  if (!token) {
    if (mrReject) mrReject(new Error("Captcha failed."));
  } else {
    if (mrResolve) mrResolve({ token, widgetId: mrWidgetId });
  }
  mrResolve = null;
  mrReject = null;
}

/**
 * Reset captcha widget after attempt.
 */
function mrResetCaptcha() {
  try {
    if (window.hcaptcha && mrWidgetId != null) {
      window.hcaptcha.reset(mrWidgetId);
    }
  } catch (_) {}
}

/**
 * Utility: show error in a panel
 */
function mrShowError(panel, msg) {
  const errorEl = panel?.querySelector(".mr-notify-error");
  if (!errorEl) return;
  errorEl.textContent = msg;
  errorEl.style.display = "block";
}
function mrClearError(panel) {
  const errorEl = panel?.querySelector(".mr-notify-error");
  if (!errorEl) return;
  errorEl.textContent = "";
  errorEl.style.display = "none";
}

/**
 * Utility: normalize product id into Shopify GID if needed
 */
function mrNormalizeProductGid(rawProductId) {
  const raw = (rawProductId || "").trim();
  if (!raw) return "";
  return raw.startsWith("gid://") ? raw : `gid://shopify/Product/${raw}`;
}

/* ---------- Ensure hCaptcha becomes available ---------- */
window.addEventListener("load", () => {
  // try once after full load (helps with async/defer timing)
  const ok = mrInitCaptcha();
  console.log("[MR] window load; hcaptcha present?", !!window.hcaptcha, "init ok?", ok);
});

/* =========================
   Notify-Me UI logic
========================= */

document.addEventListener("click", async (e) => {
  const openBtn   = e.target.closest("[data-mr-notify-open]");
  const closeBtn  = e.target.closest("[data-mr-notify-close]");
  const submitBtn = e.target.closest(".mr-notify-submit");
  const sizeBtn   = e.target.closest(".mr-size-option");

  /* ---------- SIZE SELECT (optional) ---------- */
  if (sizeBtn) {
    const size = (sizeBtn.dataset.size || "").trim();
    if (!size) return;

    const card = sizeBtn.closest(".mr-card") || sizeBtn.closest("article") || document;

    // visual state
    card.querySelectorAll(".mr-size-option").forEach(b => b.classList.remove("is-selected"));
    sizeBtn.classList.add("is-selected");

    // store selection on THIS card's notify trigger
    const trigger = card.querySelector("[data-mr-notify-open]");
    if (trigger) trigger.dataset.requestedSize = size;

    return;
  }

  /* ---------- OPEN ---------- */
  if (openBtn) {
    const panelId = openBtn.getAttribute("aria-controls");
    if (!panelId) return console.warn("[MR] Missing aria-controls on notify trigger");

    const panel = document.getElementById(panelId);
    if (!panel) return console.warn("[MR] Panel not found:", panelId);

    // close other open panels
    document.querySelectorAll(".mr-notify-modal.is-open").forEach(el => {
      if (el.id !== panelId) {
        el.classList.remove("is-open");
        el.setAttribute("aria-hidden", "true");
      }
    });

    mrClearError(panel);

    panel.classList.add("is-open");
    panel.setAttribute("aria-hidden", "false");
    panel.querySelector(".mr-notify-email")?.focus();

    return;
  }

  /* ---------- CLOSE ---------- */
  if (closeBtn) {
    const panel = closeBtn.closest(".mr-notify-modal");
    if (!panel) return;
    panel.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    mrClearError(panel);
    return;
  }

  /* ---------- SUBMIT (PLUS BUTTON) ---------- */
  if (submitBtn) {
    const panel = submitBtn.closest(".mr-notify-modal");
    if (!panel) return;

    const emailEl      = panel.querySelector(".mr-notify-email");
    const privacyEl    = panel.querySelector(".mr-notify-privacy");
    const newsletterEl = panel.querySelector(".mr-notify-newsletter");

    const email        = (emailEl?.value || "").trim();
    const privacyOk    = !!privacyEl?.checked;
    const newsletterOk = !!newsletterEl?.checked;

    mrClearError(panel);

    if (!privacyOk) return mrShowError(panel, "Please agree to the privacy policy.");
    if (!email || !email.includes("@")) return mrShowError(panel, "Please enter a valid email address.");

    const rawProductId = submitBtn.dataset.productId || "";
    const productColor = submitBtn.dataset.productColor || "";
    const productId    = mrNormalizeProductGid(rawProductId);

    if (!productId) return mrShowError(panel, "Missing product id.");

    // optional size: stored on THIS card's trigger
    const card = submitBtn.closest(".mr-card") || submitBtn.closest("article") || document;
    const trigger = card.querySelector("[data-mr-notify-open]");
    const requestedSize = (trigger?.dataset.requestedSize || "").trim() || null;

    submitBtn.disabled = true;

    try {
      // 1) captcha token
      const { token } = await getHCaptchaToken();
      console.log("[MR] captcha token length:", (token || "").length);

      // 2) send to Worker
      const resp = await fetch(MR_NOTIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          productId,
          productColor,
          requestedSize,
          newsletterOptIn: newsletterOk,
          hcaptchaToken: token,
        }),
      });

      const raw = await resp.text();
      let data = {};
      try { data = JSON.parse(raw); } catch {}

      if (!resp.ok || !data.success) {
        throw new Error(data.message || `HTTP ${resp.status} – ${raw.slice(0, 160)}`);
      }

      // clear size selection after success (optional)
      card.querySelectorAll(".mr-size-option").forEach(b => b.classList.remove("is-selected"));
      if (trigger) delete trigger.dataset.requestedSize;

      mrShowHint(panel, "Confirmed — you will be notified!");
      if (submitBtn) submitBtn.disabled = true;

        // optional: clear email + size selection immediately
        if (emailEl) emailEl.value = "";
        card.querySelectorAll(".mr-size-option").forEach(b => b.classList.remove("is-selected"));
        if (trigger) delete trigger.dataset.requestedSize;

        // close after 7 seconds
        setTimeout(() => {
        mrClosePanel(panel);
        mrClearError(panel); // hide the hint after closing, optional
        if (submitBtn) submitBtn.disabled = false;
        }, 7000);


    } catch (err) {
      console.error("[MR] notify failed:", err);
      mrShowError(panel, err?.message || "Network error.");
    } finally {
      mrResetCaptcha();
      submitBtn.disabled = false;
    }
  }
});


function mrShowHint(panel, msg) {
  const el = panel?.querySelector(".mr-notify-error");
  if (!el) return;
  el.textContent = msg;
  el.style.display = "block";
}

function mrClosePanel(panel) {
  panel.classList.remove("is-open");
  panel.setAttribute("aria-hidden", "true");
}